import { useState, useEffect, useCallback } from 'react';
import * as eventService from '../services/eventService';
import { playSuccessChime, playSoftPop } from '../utils/audio';

/**
 * useEvents hook
 * Encapsulates all state, side effects, and operations for event management.
 */
export function useEvents() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');

  // Modals state: 'add' | 'edit' | 'delete' | null
  const [activeModal, setActiveModal] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [title, setTitle] = useState('');

  // Fetch events list
  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await eventService.getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError('Tadbirlarni yuklashda xatolik yuz berdi. Iltimos qayta urinib ko\'ring.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Open modal handlers
  const openAddModal = useCallback(() => {
    playSoftPop();
    setTitle('');
    setFieldError('');
    setError('');
    setActiveModal('add');
  }, []);

  const openEditModal = useCallback((event) => {
    playSoftPop();
    setSelectedEvent(event);
    setTitle(event.title || '');
    setFieldError('');
    setError('');
    setActiveModal('edit');
  }, []);

  const openDeleteModal = useCallback((event) => {
    playSoftPop();
    setSelectedEvent(event);
    setError('');
    setActiveModal('delete');
  }, []);

  const closeModal = useCallback(() => {
    playSoftPop();
    setActiveModal(null);
    setSelectedEvent(null);
    setTitle('');
    setFieldError('');
  }, []);

  // Form submit handlers
  const handleAddEvent = useCallback(async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setFieldError('Tadbir nomi kiritilishi shart.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await eventService.addEvent({ title: title.trim() });
      playSuccessChime();
      await fetchEvents(); // Refresh list
      setActiveModal(null);
      setSelectedEvent(null);
      setTitle('');
      setFieldError('');
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Tadbir qo\'shishda xatolik yuz berdi.');
    } finally {
      setIsSubmitting(false);
    }
  }, [title, fetchEvents]);

  const handleEditEvent = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    if (!title.trim()) {
      setFieldError('Tadbir nomi kiritilishi shart.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await eventService.updateEvent(selectedEvent.id, { title: title.trim() });
      playSuccessChime();
      await fetchEvents(); // Refresh list
      setActiveModal(null);
      setSelectedEvent(null);
      setTitle('');
      setFieldError('');
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Tadbirni tahrirlashda xatolik yuz berdi.');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedEvent, title, fetchEvents]);

  const handleDeleteEvent = useCallback(async () => {
    if (!selectedEvent) return;

    setIsSubmitting(true);
    setError('');
    try {
      await eventService.deleteEvent(selectedEvent.id);
      playSuccessChime();
      await fetchEvents(); // Refresh list
      setActiveModal(null);
      setSelectedEvent(null);
      setTitle('');
      setFieldError('');
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Tadbirni o\'chirishda xatolik yuz berdi.');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedEvent, fetchEvents]);

  return {
    events,
    isLoading,
    isSubmitting,
    error,
    fieldError,
    activeModal,
    selectedEvent,
    title,
    setTitle,
    setFieldError,
    openAddModal,
    openEditModal,
    openDeleteModal,
    closeModal,
    handleAddEvent,
    handleEditEvent,
    handleDeleteEvent,
    fetchEvents,
  };
}
